import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useAllClassifiedContactPolicies } from '../../../../../queries/Contact/useAllClassifiedContactPolicies';
import { useInsurancePolicyLineOfBusinessAutocompleteQuery } from '../../../../../queries';
import { Checkbox } from '../../../Checkbox';
import { Dropdown } from '../../../Dropdown';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { styleSheet } from './styles';
import {
	groupLobsByCategory,
	hasCategoryGrouping,
	isCategoryFullySelected,
	isCategoryPartiallySelected,
	toggleCategorySelection,
} from '../lobGrouping';

export const PolicyLineOfBusinessFilterDropdown = ({
	selectedPolicyLineOfBusinessIds,
	setSelectedPolicyLineOfBusinessIds,
	anchorClassName,
	enableCategoryGrouping = false,
}: {
	selectedPolicyLineOfBusinessIds: string[];
	setSelectedPolicyLineOfBusinessIds: React.Dispatch<React.SetStateAction<string[]>>;
	anchorClassName?: string;
	enableCategoryGrouping?: boolean;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);

	const policyLineOfBusinessAutocompleteQuery = useInsurancePolicyLineOfBusinessAutocompleteQuery({
		enabled: true,
		pageSize: 100,
		fragment: '',
	});

	const policyLineOfBusinesses = React.useMemo(() => {
		return policyLineOfBusinessAutocompleteQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [policyLineOfBusinessAutocompleteQuery.data]);

	const { data: classifiedPolicies } = useAllClassifiedContactPolicies({
		contactsRequest: {},
		enabled: enableCategoryGrouping,
	});

	const lobCategories = React.useMemo(
		() => groupLobsByCategory(policyLineOfBusinesses, classifiedPolicies),
		[policyLineOfBusinesses, classifiedPolicies]
	);

	const hasCategories = hasCategoryGrouping(lobCategories);

	const dropdownMessage = React.useMemo(() => {
		if (selectedPolicyLineOfBusinessIds.length === 0) {
			return 'Line of Business';
		}
		const firstType = policyLineOfBusinesses.find(
			policyLineOfBusiness => policyLineOfBusiness.id === selectedPolicyLineOfBusinessIds[0]
		);
		return selectedPolicyLineOfBusinessIds.length === 1
			? `Has ${firstType?.name} Policy`
			: `${firstType?.name} or ${selectedPolicyLineOfBusinessIds.length - 1}`;
	}, [selectedPolicyLineOfBusinessIds, policyLineOfBusinesses]);

	const resetFilter = () => {
		setSelectedPolicyLineOfBusinessIds([]);
	};

	const onTogglePolicyLineOfBusiness = (policyLineOfBusiness: Api.IInsurancePolicyLineOfBusiness) => {
		const nextSelectedTypeIds = [...selectedPolicyLineOfBusinessIds];
		const currentIndex = selectedPolicyLineOfBusinessIds.indexOf(policyLineOfBusiness.id);

		if (currentIndex === -1) {
			nextSelectedTypeIds.push(policyLineOfBusiness.id);
		} else {
			nextSelectedTypeIds.splice(currentIndex, 1);
		}
		setSelectedPolicyLineOfBusinessIds(nextSelectedTypeIds);
	};

	const onToggleCategory = (lobsInCategory: Api.IInsurancePolicyLineOfBusiness[]) => {
		setSelectedPolicyLineOfBusinessIds(
			toggleCategorySelection(lobsInCategory, selectedPolicyLineOfBusinessIds, lob => lob.id)
		);
	};

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<span>{dropdownMessage}</span>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={open => setIsOpen(open)}
			openOnClick
		>
			<div className={css(dropdownStyles.dropdown)}>
				<div>
					{hasCategories
						? lobCategories.map(([category, lobs]) => (
								<div key={`lob-category-${category}`}>
									<div className={css(styleSheet.dropdownRow, styleSheet.categoryRow)}>
										<Checkbox
											type='large'
											checked={isCategoryFullySelected(lobs, selectedPolicyLineOfBusinessIds, lob => lob.id)}
											partial={isCategoryPartiallySelected(lobs, selectedPolicyLineOfBusinessIds, lob => lob.id)}
											id={`policy-board-lob-category-${category}`}
											onChange={() => onToggleCategory(lobs)}
										/>
										<span className={css(styleSheet.categoryLabel)}>{category}</span>
									</div>
									{lobs.map(policyLineOfBusiness => (
										<div
											className={css(styleSheet.dropdownRow, styleSheet.nestedRow)}
											key={`policy-type-select-${policyLineOfBusiness.id}`}
										>
											<Checkbox
												type='large'
												checked={selectedPolicyLineOfBusinessIds.indexOf(policyLineOfBusiness.id) > -1}
												id={policyLineOfBusiness.id}
												onChange={() => onTogglePolicyLineOfBusiness(policyLineOfBusiness)}
											/>
											<span>{policyLineOfBusiness.name}</span>
										</div>
									))}
								</div>
							))
						: policyLineOfBusinesses.map(policyLineOfBusiness => (
								<div className={css(styleSheet.dropdownRow)} key={`policy-type-select-${policyLineOfBusiness.id}`}>
									<Checkbox
										type='large'
										checked={selectedPolicyLineOfBusinessIds.indexOf(policyLineOfBusiness.id) > -1}
										id={policyLineOfBusiness.id}
										onChange={() => onTogglePolicyLineOfBusiness(policyLineOfBusiness)}
									/>
									<span>{policyLineOfBusiness.name}</span>
								</div>
							))}
				</div>

				{policyLineOfBusinessAutocompleteQuery.hasNextPage &&
					!policyLineOfBusinessAutocompleteQuery.isFetchingNextPage && (
						<Waypoint onEnter={() => policyLineOfBusinessAutocompleteQuery.fetchNextPage()} />
					)}
				<div className={css(dropdownStyles.dropdownSeparator)} />

				<button onClick={resetFilter}>Reset Filter</button>
			</div>
		</Dropdown>
	);
};
